
import React, { useState } from 'react';
import { useCartPromotions } from '../../cart/hooks/useCartPromotions';

/**
 * Calculates estimated price after applying a cart promotion discount.
 * @param {number} basePrice - Current selling price (precioCompra with BC discounts)
 * @param {number} discountPercentage - Cart promotion discount to apply
 * @returns {number}
 */
const calcPromoPrice = (basePrice, discountPercentage) => {
    if (!basePrice || !discountPercentage) return basePrice;
    return basePrice * (1 - discountPercentage / 100);
};

/**
 * @param {{
 *   product: any,
 *   currentQuantity: number,
 *   basePrice: number,
 *   onSelectPromotion: (promo: any) => void
 * }} props
 */
export const ProductPromotions = ({ product, currentQuantity, basePrice, onSelectPromotion }) => {
    const { promotions, loading } = useCartPromotions();
    const [selectedPromoId, setSelectedPromoId] = useState(null);

    if (loading || !promotions || promotions.length === 0) return null;

    const handleSelection = (promo) => {
        setSelectedPromoId(promo.id);
        if (onSelectPromotion) {
            onSelectPromotion(promo);
        }
    };

    const handleClear = () => {
        setSelectedPromoId(null);
        if (onSelectPromotion) onSelectPromotion(null);
    };

    return (
        <div className="product-promotions border p-3 rounded mb-3">
            <h6 className="text-primary mb-3">🏷️ Ofertas exclusivas</h6>
            {promotions.map(promo => {
                const minQty = promo.minQuantity ?? 0;
                const isActive = minQty === 0 || currentQuantity >= minQty;
                const missingQty = minQty - currentQuantity;
                const promoPrice = promo.discountPercentage
                    ? calcPromoPrice(basePrice, promo.discountPercentage)
                    : (promo.fixedPrice ?? null);

                return (
                    <div
                        key={promo.id}
                        className="d-flex align-items-start justify-content-between mb-2 p-2 border rounded"
                        style={{ backgroundColor: selectedPromoId === promo.id ? '#f0f9f0' : 'white', cursor: 'pointer' }}
                        onClick={() => handleSelection(promo)}
                    >
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                            <input
                                type="radio"
                                id={`promo-${promo.id}`}
                                name="product-promotions"
                                checked={selectedPromoId === promo.id}
                                onChange={() => handleSelection(promo)}
                                style={{ marginTop: '3px', flexShrink: 0 }}
                            />
                            <label htmlFor={`promo-${promo.id}`} style={{ cursor: 'pointer', marginBottom: 0 }}>
                                <span className="fw-bold d-block">{promo.description}</span>
                                <span className="text-muted small">
                                    {promo.discountPercentage ? `${promo.discountPercentage}% dto.` : ''}
                                    {promo.fixedPrice ? `Precio fijo ${promo.fixedPrice.toFixed(2)}€` : ''}
                                    {minQty > 0 ? ` · Mín. ${minQty} uds` : ''}
                                </span>
                            </label>
                        </div>

                        <div className="text-end ms-3" style={{ minWidth: '120px' }}>
                            {isActive ? (
                                <>
                                    <span className="badge bg-success mb-1 d-block">✅ Activa</span>
                                    {promoPrice != null && (
                                        <span className="text-success fw-bold small">
                                            {promoPrice.toFixed(2)}€/ud est.
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span className="badge bg-warning text-dark mb-1 d-block">
                                        Añade {missingQty} más
                                    </span>
                                    {promoPrice != null && (
                                        <span className="text-muted small">
                                            {promoPrice.toFixed(2)}€/ud con dto.
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                );
            })}

            <div
                className="d-flex align-items-center mb-0 p-2"
                style={{ cursor: 'pointer' }}
                onClick={handleClear}
            >
                <input
                    type="radio"
                    id="promo-none"
                    name="product-promotions"
                    checked={selectedPromoId === null}
                    onChange={handleClear}
                    className="me-2"
                />
                <label htmlFor="promo-none" className="text-muted small mb-0" style={{ cursor: 'pointer' }}>
                    Sin promoción adicional
                </label>
            </div>
        </div>
    );
};
